import React, { useState, useEffect } from 'react';
import { X, Calendar, Activity, Trash2, Plus, Pill, Check, Search, MapPin, UserPlus, Smile, Flame, Wind, Download } from 'lucide-react'; // Added Download
import { db } from '../firebase';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import './ChatBot.css';

// Simple SVG Line Chart Component
const SimpleLineChart = ({ data, color = "#3182ce" }) => {
    if (!data || data.length < 2) return <p style={{ textAlign: 'center', color: '#718096', fontSize: '0.9rem' }}>Add more data points to see the trend.</p>;

    const width = 300;
    const height = 150;
    const padding = 20;

    const values = data.map(d => parseFloat(d.value));
    const min = Math.min(...values);
    const max = Math.max(...values);

    // Normalize points
    const points = data.map((d, i) => {
        const x = padding + (i / (data.length - 1)) * (width - 2 * padding);
        const y = height - padding - ((parseFloat(d.value) - min) / (max - min || 1)) * (height - 2 * padding);
        return `${x},${y}`;
    }).join(' ');

    return (
        <div style={{ width: '100%', overflowX: 'auto', display: 'flex', justifyContent: 'center' }}>
            <svg width={width} height={height} style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '10px' }}>
                <polyline
                    fill="none"
                    stroke={color}
                    strokeWidth="3"
                    points={points}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                {data.map((d, i) => {
                    const x = padding + (i / (data.length - 1)) * (width - 2 * padding);
                    const y = height - padding - ((parseFloat(d.value) - min) / (max - min || 1)) * (height - 2 * padding);
                    return (
                        <circle key={i} cx={x} cy={y} r="4" fill="white" stroke={color} strokeWidth="2" />
                    );
                })}
            </svg>
        </div>
    );
};

const DashboardModal = ({ isOpen, onClose, userProfile, onBook }) => {
    const [activeTab, setActiveTab] = useState('appointments');
    const [appointments, setAppointments] = useState([]);
    const [vitals, setVitals] = useState([]);
    const [newVital, setNewVital] = useState({ date: '', value: '' });

    // BMI State
    const [height, setHeight] = useState(localStorage.getItem('user_height') || '');
    const [bmi, setBmi] = useState(null);

    // Meds State
    const [meds, setMeds] = useState([]);
    const [takenMeds, setTakenMeds] = useState({});

    // Directory State
    const [doctors, setDoctors] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Wellness State
    const [mood, setMood] = useState(null);
    const [streak, setStreak] = useState(0);
    const [zenMode, setZenMode] = useState(false); // Breathing exercise toggle

    // Load Vitals & Height
    useEffect(() => {
        const savedVitals = localStorage.getItem('user_vitals');
        if (savedVitals) setVitals(JSON.parse(savedVitals));
    }, []);

    // Calculate BMI whenever height or latest weight changes
    useEffect(() => {
        if (height && vitals.length > 0) {
            const latestWeight = parseFloat(vitals[vitals.length - 1].value);
            const hM = parseFloat(height) / 100;
            if (latestWeight > 0 && hM > 0) {
                const bmiVal = (latestWeight / (hM * hM)).toFixed(1);
                setBmi(bmiVal);
                localStorage.setItem('user_height', height);
            }
        }
    }, [height, vitals]);

    // Parse Medications from Profile
    useEffect(() => {
        if (userProfile && userProfile.medications) {
            const list = userProfile.medications.split(/,|;|\n/).map(m => m.trim()).filter(m => m);
            setMeds(list);

            // Load today's taken status
            const today = new Date().toISOString().split('T')[0];
            const savedTaken = JSON.parse(localStorage.getItem(`meds_taken_${today}`)) || {};
            setTakenMeds(savedTaken);
        }
    }, [userProfile]);

    const toggleMedTaken = (med) => {
        const today = new Date().toISOString().split('T')[0];
        const newStatus = { ...takenMeds, [med]: !takenMeds[med] };
        setTakenMeds(newStatus);
        localStorage.setItem(`meds_taken_${today}`, JSON.stringify(newStatus));
    };

    const getBmiStatus = (val) => {
        if (val < 18.5) return { label: 'Underweight', color: '#3182ce' };
        if (val < 25) return { label: 'Healthy', color: '#38a169' };
        if (val < 30) return { label: 'Overweight', color: '#d69e2e' };
        return { label: 'Obese', color: '#e53e3e' };
    };

    // Load Mood & Streak on Mount
    useEffect(() => {
        if (!isOpen) return;

        // Load today's mood
        const today = new Date().toISOString().split('T')[0];
        const savedMood = localStorage.getItem(`mood_${today}`);
        if (savedMood) setMood(savedMood);

        // Calculate Streak
        let currentStreak = 0;
        let dateToCheck = new Date();

        // Simple logic: Check backwards for 'activity' (mood logged)
        while (true) {
            const dateStr = dateToCheck.toISOString().split('T')[0];
            const hasActivity = localStorage.getItem(`mood_${dateStr}`) || localStorage.getItem(`meds_taken_${dateStr}`);

            if (hasActivity) {
                currentStreak++;
                dateToCheck.setDate(dateToCheck.getDate() - 1);
            } else {
                // If today is empty, don't break streak immediately if yesterday was active
                if (dateStr === today && currentStreak === 0) {
                    dateToCheck.setDate(dateToCheck.getDate() - 1);
                    continue;
                }
                break;
            }
        }
        setStreak(currentStreak);
    }, [isOpen, mood, takenMeds]);

    const handleMoodSelect = (selectedMood) => {
        const today = new Date().toISOString().split('T')[0];
        setMood(selectedMood);
        localStorage.setItem(`mood_${today}`, selectedMood);
    };

    // calculate Adherence for last 7 days
    const getAdherence = () => {
        let takenCount = 0;
        for (let i = 0; i < 7; i++) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const dateStr = d.toISOString().split('T')[0];
            const taken = JSON.parse(localStorage.getItem(`meds_taken_${dateStr}`)) || {};
            // If any med taken that day, count as adherence (simplified)
            if (Object.values(taken).some(v => v)) takenCount++;
        }
        return Math.round((takenCount / 7) * 100);
    };

    const handleExport = () => {
        const data = {
            profile: userProfile,
            vitals: vitals,
            appointments: appointments,
            meds: meds,
            exportDate: new Date().toISOString()
        };
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pulsenote_health_data_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    // Load Doctors for Directory
    useEffect(() => {
        if (activeTab !== 'directory') return;
        const fetchDocs = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "doctors"));
                const docs = [];
                querySnapshot.forEach((doc) => docs.push({ id: doc.id, ...doc.data() }));
                setDoctors(docs);
            } catch (e) { console.error("Error fetching doctors", e); }
        };
        fetchDocs();
    }, [activeTab]);

    const filteredDoctors = doctors.filter(doc =>
        doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.specialty.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Load Appointments from Firestore
    useEffect(() => {
        if (!isOpen || !userProfile || !userProfile.name) return;
        const fetchAppts = async () => {
            try {
                const q = query(collection(db, "appointments"), where("patientName", "==", userProfile.name));
                const querySnapshot = await getDocs(q);
                const appts = [];
                querySnapshot.forEach((doc) => appts.push({ id: doc.id, ...doc.data() }));
                setAppointments(appts);
            } catch (e) { console.error("Error fetching appointments", e); }
        };
        fetchAppts();
    }, [isOpen, userProfile]);

    const handleCancel = async (id) => {
        if (!window.confirm("Cancel this appointment?")) return;
        try {
            await deleteDoc(doc(db, "appointments", id));
            setAppointments(prev => prev.filter(a => a.id !== id));
        } catch (e) { console.error(e); }
    };

    const handleAddVital = (e) => {
        e.preventDefault();
        if (!newVital.date || !newVital.value) return;
        const updated = [...vitals, newVital].sort((a, b) => new Date(a.date) - new Date(b.date));
        setVitals(updated);
        localStorage.setItem('user_vitals', JSON.stringify(updated));
        setNewVital({ date: '', value: '' });
    };

    const handleClearVitals = () => {
        if (window.confirm("Clear all vitals history?")) {
            setVitals([]);
            localStorage.removeItem('user_vitals');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="disclaimer-overlay" style={{ zIndex: 2000 }}>
            <div className="chatbot-window" style={{ position: 'relative', width: '90%', maxWidth: '400px', height: '550px', maxHeight: '90vh' }}>
                <div className="chatbot-header">
                    <div className="chatbot-header-title">
                        <Activity size={20} />
                        <h3>Health Hub</h3>
                        {streak > 0 && (
                            <div style={{ marginLeft: '10px', fontSize: '0.85rem', background: '#fffaf0', color: '#ed8936', padding: '2px 8px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #ed8936' }}>
                                <Flame size={12} fill="#ed8936" /> {streak} Day Streak
                            </div>
                        )}
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '5px' }}>
                        <button onClick={handleExport} className="action-btn" title="Export Data"><Download size={18} /></button>
                        <button onClick={onClose} className="action-btn"><X size={20} /></button>
                    </div>
                </div>

                {/* TABS */}
                <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)' }}>
                    <button
                        onClick={() => setActiveTab('appointments')}
                        style={{ flex: 1, padding: '12px', background: activeTab === 'appointments' ? 'var(--bg-secondary)' : 'transparent', border: 'none', borderBottom: activeTab === 'appointments' ? '2px solid var(--primary-color)' : 'none', fontWeight: '600', color: 'var(--text-color)', cursor: 'pointer' }}
                    >
                        Appointments
                    </button>
                    <button
                        onClick={() => setActiveTab('vitals')}
                        style={{ flex: 1, padding: '12px', background: activeTab === 'vitals' ? 'var(--bg-secondary)' : 'transparent', border: 'none', borderBottom: activeTab === 'vitals' ? '2px solid var(--primary-color)' : 'none', fontWeight: '600', color: 'var(--text-color)', cursor: 'pointer' }}
                    >
                        Vitals Tracker
                    </button>
                    <button
                        onClick={() => setActiveTab('directory')}
                        style={{ flex: 1, padding: '12px', background: activeTab === 'directory' ? 'var(--bg-secondary)' : 'transparent', border: 'none', borderBottom: activeTab === 'directory' ? '2px solid var(--primary-color)' : 'none', fontWeight: '600', color: 'var(--text-color)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
                    >
                        <Search size={14} /> Find
                    </button>
                    <button
                        onClick={() => setActiveTab('wellness')}
                        style={{ flex: 1, padding: '12px', background: activeTab === 'wellness' ? 'var(--bg-secondary)' : 'transparent', border: 'none', borderBottom: activeTab === 'wellness' ? '2px solid var(--primary-color)' : 'none', fontWeight: '600', color: 'var(--text-color)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
                    >
                        <Smile size={14} /> Zen
                    </button>
                </div>

                <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
                    {activeTab === 'appointments' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {appointments.length === 0 ? (
                                <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>No upcoming appointments.</p>
                            ) : (
                                appointments.map(appt => (
                                    <div key={appt.id} className="doctor-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <h4>{appt.doctorName}</h4>
                                            <p><Calendar size={12} /> {appt.date} at {appt.time}</p>
                                            <span style={{ fontSize: '0.8rem', padding: '2px 6px', borderRadius: '4px', background: '#c6f6d5', color: '#22543d' }}>Confirmed</span>
                                        </div>
                                        <button onClick={() => handleCancel(appt.id)} style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer' }}><Trash2 size={18} /></button>
                                    </div>
                                ))
                            )}
                        </div>
                    ) : activeTab === 'vitals' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div style={{ textAlign: 'center' }}>
                                <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-color)' }}>Weight / BP Trend</h4>
                                <SimpleLineChart data={vitals} />
                            </div>

                            <form onSubmit={handleAddVital} style={{ background: 'var(--bg-secondary)', padding: '15px', borderRadius: '8px' }}>
                                <h5 style={{ margin: '0 0 10px 0', color: 'var(--text-color)' }}>Add Reading</h5>
                                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                    <input
                                        type="date"
                                        value={newVital.date}
                                        onChange={(e) => setNewVital({ ...newVital, date: e.target.value })}
                                        style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--input-bg)', color: 'var(--text-color)' }}
                                        required
                                    />
                                    <input
                                        type="number"
                                        placeholder="Value (e.g. kg)"
                                        value={newVital.value}
                                        onChange={(e) => setNewVital({ ...newVital, value: e.target.value })}
                                        style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--input-bg)', color: 'var(--text-color)' }}
                                        required
                                    />
                                </div>
                                <button type="submit" className="book-btn" style={{ width: '100%', justifyContent: 'center' }}>
                                    <Plus size={16} /> Add Entry
                                </button>
                            </form>

                            {vitals.length > 0 && (
                                <button onClick={handleClearVitals} style={{ margin: '0 auto', display: 'block', color: 'var(--text-secondary)', background: 'none', border: 'none', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}>
                                    Reset Data
                                </button>
                            )}
                        </div>
                    ) : activeTab === 'wellness' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {/* MOOD TRACKER */}
                            <div style={{ background: 'var(--bg-secondary)', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                                <h4 style={{ margin: '0 0 15px 0' }}>How are you feeling today?</h4>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                                    {['😢', '😐', '🙂', '😀'].map((m) => (
                                        <button
                                            key={m}
                                            onClick={() => handleMoodSelect(m)}
                                            style={{ fontSize: '2rem', background: mood === m ? 'var(--primary-color)' : 'var(--bg-primary)', border: mood === m ? '2px solid var(--primary-color)' : '1px solid var(--border-color)', borderRadius: '50%', width: '60px', height: '60px', cursor: 'pointer', transition: 'all 0.2s' }}
                                        >
                                            {m}
                                        </button>
                                    ))}
                                </div>
                                {mood && <p style={{ marginTop: '10px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Mood logged!</p>}
                            </div>

                            {/* ZEN MODE */}
                            <div style={{ background: 'linear-gradient(135deg, #a0aec0 0%, #718096 100%)', padding: '20px', borderRadius: '8px', textAlign: 'center', color: 'white' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '10px' }}>
                                    <Wind size={24} />
                                    <h4 style={{ margin: 0 }}>Zen Mode: Box Breathing</h4>
                                </div>

                                {!zenMode ? (
                                    <button
                                        onClick={() => setZenMode(true)}
                                        style={{ background: 'white', color: '#2d3748', border: 'none', padding: '10px 20px', borderRadius: '20px', fontWeight: 'bold', cursor: 'pointer', marginTop: '10px' }}
                                    >
                                        Start Session (1 min)
                                    </button>
                                ) : (
                                    <div className="breathing-circle" style={{ margin: '20px auto', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.3)', border: '4px solid white', animation: 'breathe 4s infinite ease-in-out' }}>
                                        <style>{`@keyframes breathe { 0%, 100% { transform: scale(1); opacity: 0.5; } 50% { transform: scale(1.5); opacity: 1; } }`}</style>
                                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>
                                            In... Out...
                                        </div>
                                    </div>
                                )}
                                {zenMode && (
                                    <button onClick={() => setZenMode(false)} style={{ background: 'transparent', border: '1px solid white', color: 'white', padding: '5px 15px', borderRadius: '15px', fontSize: '0.8rem', marginTop: '10px', cursor: 'pointer' }}>Stop</button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardModal;
